import { z } from 'zod';
import { EmployeePersonalityCommunicationStyle } from 'src/api/generated/models/EmployeePersonalityCommunicationStyle';

export const employeeMetadataSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  description: z.string().optional(),
});

export const communicationStyleEnum = z.nativeEnum(
  EmployeePersonalityCommunicationStyle
);

export const employeePersonalitySchema = z.object({
  communicationStyle: communicationStyleEnum,
  name: z.string(),
  customTraits: z.string().optional(),
});

export const toolConfigurationSchema = z.object({
  name: z.string(),
  alias: z
    .preprocess(
      (value) =>
        typeof value === 'string' && value.trim() === '' ? undefined : value,
      z
        .string()
        .regex(
          /^[a-zA-Z0-9._-]+$/,
          // TODO: check when implementing i18n
          'Alias inválido: usa letras, números, punto, guion y guion bajo'
        )
        .min(1)
        .optional()
    )
    .optional(),
  usageInstructions: z.string().optional(),
  configuredInputs: z.record(z.string(), z.unknown()).optional(),
});

export const employeeVersionSchema = z
  .object({
    jobTitle: z.string().optional(),
    // TODO: check when implementing i18n
    objectives: z.array(z.string()).min(1, 'El objetivo es requerido'),
    constraints: z.array(z.string()).optional(),
    personality: employeePersonalitySchema,
    knowledgeBase: z.string().optional(),
    tools: z.array(toolConfigurationSchema).default([]),
  })
  .superRefine((val, ctx) => {
    const aliases = (val.tools || [])
      .map((t) => t.alias)
      .filter((a): a is string => typeof a === 'string');
    const duplicateAliases = aliases.filter((a, i) => aliases.indexOf(a) !== i);
    if (duplicateAliases.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tools'],
        // TODO: check when implementing i18n
        message: `Alias de herramientas duplicados: ${[...new Set(duplicateAliases)].join(', ')}`,
      });
    }
  });

export const employeeFormSchema = z.object({
  metadata: employeeMetadataSchema,
  definition: employeeVersionSchema,
});

export type EmployeeFormInputValues = z.input<typeof employeeFormSchema>;
export type EmployeeFormValues = z.output<typeof employeeFormSchema>;

export const EmployeeFormPath = {
  // Root level paths
  METADATA: 'metadata' as const,
  DEFINITION: 'definition' as const,

  // Metadata paths
  METADATA_NAME: 'metadata.name' as const,
  METADATA_DESCRIPTION: 'metadata.description' as const,

  // Definition paths
  DEFINITION_JOB_TITLE: 'definition.jobTitle' as const,
  DEFINITION_OBJECTIVES: 'definition.objectives' as const,
  DEFINITION_CONSTRAINTS: 'definition.constraints' as const,
  DEFINITION_KNOWLEDGE_BASE: 'definition.knowledgeBase' as const,
  DEFINITION_TOOLS: 'definition.tools' as const,

  // Personality paths
  DEFINITION_PERSONALITY_COMMUNICATION_STYLE:
    'definition.personality.communicationStyle' as const,
  DEFINITION_PERSONALITY_NAME: 'definition.personality.name' as const,
  DEFINITION_PERSONALITY_CUSTOM_TRAITS:
    'definition.personality.customTraits' as const,
} as const;

export type TabValue = 'overview' | 'tools' | 'workspace';
